import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../styles/panel/user/manageShop/ShopCardInfo.css";
import "iranianbanklogos/dist/ibl.css";
import {
  getCart,
  saveStoreCard,
} from "../../../../api/product_management/cartApi";
import toast, { Toaster } from "react-hot-toast";
import { detectBank } from "./banks";
import { BACK_ICON } from "./shared/EditShopShared";

/* ---- آیکن‌های خطی هماهنگ با ManageShops ---- */
const Icon = ({ children, className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const ICONS = {
  check: <polyline points="20 6 9 17 4 12" />,
  close: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  ),
};

/* ---- ابزارهای شماره کارت ---- */

// اعداد فارسی/عربی → انگلیسی
const toEnglishDigits = (str = "") =>
  String(str)
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

const onlyDigits = (str) => toEnglishDigits(str).replace(/\D/g, "");

// 6037991234567890 → 6037 9912 3456 7890
const formatCard = (digits) => (digits.match(/.{1,4}/g) || []).join(" ");

// الگوریتم Luhn برای بررسی صحت شماره کارت
const isValidLuhn = (digits) => {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let n = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
};

// تیره کردن یه رنگ hex برای گرادینت اواتار (بدون وابستگی به تابع CSS جدید color-mix)
const shadeColor = (hex, amount) => {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const num = parseInt(full, 16);
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const r = clamp(((num >> 16) & 255) * (1 - amount));
  const g = clamp(((num >> 8) & 255) * (1 - amount));
  const b = clamp((num & 255) * (1 - amount));
  return `rgb(${r}, ${g}, ${b})`;
};

/* لوگوی بانک؛ لوگوی واقعی از iranianbanklogos، وگرنه اواتار گرادیانت با حروف اول اسم بانک */
const BankLogo = ({ bank, size = 32 }) => {
  const style = { width: size, height: size };

  if (!bank.logo) {
    const initials = bank.short.slice(0, 2);
    return (
      <span
        className="sc-bank-logo sc-bank-logo--fallback"
        style={{
          ...style,
          background: `linear-gradient(135deg, ${bank.color}, ${shadeColor(bank.color, 0.45)})`,
          fontSize: size * 0.38,
        }}
        title={bank.name}
        aria-hidden="true"
      >
        {initials}
      </span>
    );
  }

  return (
    <span
      className="sc-bank-logo sc-bank-logo--real"
      style={style}
      title={bank.name}
    >
      <i
        className={`ibl64 ibl-${bank.logo}`}
        style={{ transform: `scale(${size / 64})` }}
        aria-hidden="true"
      />
    </span>
  );
};

export default function ShopCardInfo() {
  const navigate = useNavigate();
  const { shopId } = useParams();

  const [cardNumber, setCardNumber] = useState(""); // فقط ارقام انگلیسی
  const [holder, setHolder] = useState("");
  const [touched, setTouched] = useState({ card: false, holder: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [feedback, setFeedback] = useState(null); // فقط برای خطای دریافت اطلاعات

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await getCart(shopId);
        const data = res?.data;
        if (!cancelled && data && data.number) {
          setCardNumber(onlyDigits(data.number));
          setHolder(data.owner || "");
          setAlreadySaved(true);
        }
      } catch (err) {
        // 404 یعنی هنوز کارتی ثبت نشده؛ بقیه‌ی خطاها رو نشون میدیم
        if (!cancelled && err?.response?.status !== 404) {
          setFeedback({
            type: "error",
            text: "دریافت اطلاعات کارت با مشکل مواجه شد. دوباره تلاش کن.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  const bank = useMemo(() => detectBank(cardNumber), [cardNumber]);

  const cardError = useMemo(() => {
    if (!touched.card) return "";
    if (cardNumber.length === 0) return "شماره کارت رو وارد کن.";
    if (cardNumber.length !== 16) return "شماره کارت باید ۱۶ رقم باشه.";
    if (!isValidLuhn(cardNumber))
      return "این شماره کارت معتبر نیست. ارقام رو دوباره چک کن.";
    return "";
  }, [cardNumber, touched.card]);

  const holderError = useMemo(() => {
    if (!touched.holder) return "";
    if (holder.trim().length < 3) return "نام صاحب کارت رو وارد کن.";
    return "";
  }, [holder, touched.holder]);

  const handleCardChange = (e) => {
    setFeedback(null);
    setCardNumber(onlyDigits(e.target.value).slice(0, 16));
  };

  const handleHolderChange = (e) => {
    setFeedback(null);
    setHolder(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ card: true, holder: true });

    const cardOk = cardNumber.length === 16 && isValidLuhn(cardNumber);
    const holderOk = holder.trim().length >= 3;
    if (!cardOk || !holderOk) return;

    try {
      setSaving(true);
      setFeedback(null);
      await saveStoreCard({
        store_id: Number(shopId),
        number: cardNumber,
        owner: holder.trim(),
      });
      setAlreadySaved(true);
      toast.success("اطلاعات کارت با موفقیت ذخیره شد.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "ذخیره‌ی اطلاعات کارت انجام نشد. دوباره تلاش کن.",
      );
    } finally {
      setSaving(false);
    }
  };

  const previewNumber = formatCard(cardNumber.padEnd(16, "•"));

  return (
    <div className="shop-card-page">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "inherit",
            direction: "rtl",
            fontSize: "13.5px",
          },
        }}
      />
      <div className="sc-head-cart">
        <button
          type="button"
          className="esp-back-btn"
          onClick={() => navigate(-1)}
        >
          <Icon>{BACK_ICON}</Icon>
        </button>
        <div>
          <h1>شماره کارت فروشگاه</h1>
          <p>
            شماره کارتی که پرداخت‌های این فروشگاه بهش واریز میشه رو ثبت کن.
            <span className="sc-shop-id"> فروشگاه #{shopId}</span>
          </p>
        </div>
      </div>

      {loading ? (
        <div className="sc-loading">
          <p>در حال دریافت اطلاعات کارت...</p>
        </div>
      ) : (
        <div className="sc-layout">
          {/* پیش‌نمایش کارت */}
          <div className="sc-preview-wrap">
            <div
              className={`sc-preview ${bank ? "sc-preview--known" : ""}`}
              aria-hidden="true"
            >
              <div className="sc-preview-top">
                <span className="sc-preview-bank">
                  {bank && (
                    <span className="sc-preview-logo">
                      <BankLogo bank={bank} size={34} />
                    </span>
                  )}
                  {bank ? bank.name : "نام بانک"}
                </span>
                <span className="sc-preview-chip" />
              </div>
              <div className="sc-preview-number">{previewNumber}</div>
              <div className="sc-preview-bottom">
                <span className="sc-preview-caption">صاحب کارت</span>
                <span className="sc-preview-holder">
                  {holder.trim() || "نام و نام خانوادگی"}
                </span>
              </div>
            </div>
          </div>

          {/* فرم */}
          <form className="sc-form" onSubmit={handleSubmit} noValidate>
            <div className="sc-field">
              <label htmlFor="sc-card-number" className="sc-label">
                شماره کارت
              </label>
              <input
                id="sc-card-number"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                dir="ltr"
                className={`sc-input sc-input--mono ${
                  cardError ? "sc-input--error" : ""
                }`}
                value={formatCard(cardNumber)}
                onChange={handleCardChange}
                onBlur={() => setTouched((t) => ({ ...t, card: true }))}
                placeholder="0000 0000 0000 0000"
                maxLength={19}
                aria-invalid={Boolean(cardError)}
                aria-describedby={cardError ? "sc-card-error" : undefined}
              />
              {cardError ? (
                <span className="sc-error" id="sc-card-error" role="alert">
                  {cardError}
                </span>
              ) : (
                bank && (
                  <span className="sc-hint">
                    <BankLogo bank={bank} size={22} />
                    {bank.name}
                  </span>
                )
              )}
            </div>

            <div className="sc-field">
              <label htmlFor="sc-card-holder" className="sc-label">
                نام صاحب کارت
              </label>
              <input
                id="sc-card-holder"
                type="text"
                autoComplete="off"
                className={`sc-input ${holderError ? "sc-input--error" : ""}`}
                value={holder}
                onChange={handleHolderChange}
                onBlur={() => setTouched((t) => ({ ...t, holder: true }))}
                placeholder="مثلاً علی رضایی"
                maxLength={60}
                aria-invalid={Boolean(holderError)}
                aria-describedby={holderError ? "sc-holder-error" : undefined}
              />
              {holderError && (
                <span className="sc-error" id="sc-holder-error" role="alert">
                  {holderError}
                </span>
              )}
            </div>

            <div className="sc-note">
              <Icon>{ICONS.shield}</Icon>
              <p>
                فقط شماره کارت و نام صاحب کارت لازمه. رمز کارت، CVV2 و تاریخ
                انقضا رو هیچ‌جا وارد نکن.
              </p>
            </div>

            {feedback && (
              <div
                className={`sc-feedback sc-feedback--${feedback.type}`}
                role={feedback.type === "error" ? "alert" : "status"}
              >
                <Icon>
                  {feedback.type === "success" ? ICONS.check : ICONS.alert}
                </Icon>
                {feedback.text}
              </div>
            )}

            <button type="submit" className="sc-submit" disabled={saving}>
              {saving
                ? "در حال ذخیره..."
                : alreadySaved
                  ? "ذخیره تغییرات"
                  : "ثبت شماره کارت"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
